import { useState } from 'react';

import { useUpdateEffect } from 'usehooks-ts';

import { apiSaveCart, LOCAL_STORAGE_CART_ID, LOCAL_STORAGE_CART_ID_DEFVAL } from '../constants';
import PARTNER_TAG from '../enums/EnumPartnerTag';
import { gtmPushAddSessionToCart } from '../gtm';
import { devMessage } from '../settings/settings';
import { findArrayItemById } from '../utils/arrayUtil';
import { handleParticipantDataForBackend } from '../utils/business/participants';
import { getOptionPrice, getVariantSelectedPrice, STANDARD_PRICE_SELECT_VALUE } from '../utils/business/prices';
import { isValidQuantityNumber } from '../utils/numberUtil';
import { getReferrerHost, REQUEST_STATUS } from '../utils/requestUtil';
import { getValidUuidOrNull } from '../utils/strUtil';
import useDeleteFromCart from './useDeleteFromCart';
import usePost from './usePost';
import useReadCartGroupId from './useReadCartGroupId';
import useReadSelectedStaffFilterValue from './useReadSelectedStaffFilterValue';
import useReadTeam from './useReadTeam';
import useWriteStorageBookingEngine from './useWriteStorageBookingEngine';

const usePostCart = receivedStoredCartId => {
  const { teamId, teamCurrency } = useReadTeam();
  const [preCartData, setPreCartData] = useState(null);
  const [formattedData, setFormattedData] = useState(null);
  const { data, status, executePost, resetPost } = usePost(apiSaveCart(), formattedData);

  // eslint-disable-next-line no-unused-vars
  const [storedCartId, setStoredCartId] = useWriteStorageBookingEngine(LOCAL_STORAGE_CART_ID, LOCAL_STORAGE_CART_ID_DEFVAL);

  // These are used for the edit feature
  const { setItemIdsToDelete, status: cartDeleteStatus } = useDeleteFromCart(storedCartId, true);
  const [preCartDataForEdit, setPreCartDataForEdit] = useState(null);

  // This is for when we select staffs in the BE's filters
  const { selectedStaff } = useReadSelectedStaffFilterValue();

  // This is only used for saving items with P3 shortcut
  const highestCartGroupId = useReadCartGroupId();

  const writeCart = newPreCartData => {
    setPreCartData(newPreCartData);
  };

  const editCart = newPreCartData => {
    if (JSON.stringify(newPreCartData) === JSON.stringify(preCartData)) {
      return false;
    }

    setPreCartDataForEdit(newPreCartData);

    // This change of state will trigger the delete
    setItemIdsToDelete(newPreCartData.itemsToDeleteForEdit);
    return true;
  };

  const resetWriteCart = () => {
    setPreCartData(null);
    resetPost();
  };

  useUpdateEffect(() => {
    if (cartDeleteStatus === REQUEST_STATUS.OK) {
      setPreCartData(preCartDataForEdit);
    }
  }, [cartDeleteStatus]);

  // Only trigger the format if the actual state has changed !
  useUpdateEffect(() => {
    if (preCartData === null) return;

    if (preCartData.availabilities && preCartData.availabilities.length > 0) {
      setFormattedData(
        formatPreCartDataForBooking(
          preCartData.availabilities,
          preCartData.canUseSameSessionShortcut,
          highestCartGroupId,
          teamId,
          receivedStoredCartId,
          selectedStaff
        )
      );
    } else {
      setFormattedData(formatPreCartDataForShop(preCartData.shopResource, preCartData.shopResourceChild, teamId, receivedStoredCartId));
    }
  }, [JSON.stringify(preCartData)]);

  useUpdateEffect(() => {
    if (preCartData == null) return; // react reload

    // Push only on normal add, not on edit
    if (cartDeleteStatus === REQUEST_STATUS.LOADING) {
      gtmPushAddSessionToCart(preCartData, teamCurrency);
    }
    executePost();
  }, [formattedData]);

  useUpdateEffect(() => {
    if (!data || !data.order) return;
    setStoredCartId(data.order.id);
  }, [data]);

  return { data, status, writeCart, editCart, resetWriteCart };
};

export default usePostCart;

// During multi select we apply the variant to a single availability
// And at this time when sending data, we will apply the variant to all availabilities selected
const formatPreCartDataForBooking = (allAvailabilities, canUseSameSessionShortcut, highestCartGroupId, teamId, storedCartId, selectedStaff) => {
  const formattedData = getInitialFormattedData(teamId, storedCartId);

  let variantId = 0;
  let firstVariant = null;
  let cartGroupId = highestCartGroupId ?? 0;
  const ffvLicenceMap = {};
  for (const availability of allAvailabilities) {
    cartGroupId++;

    // When we use the P3 shortcut to book the same session several times, the variants data isnt shared between availabilities
    // Otherwise (price per participant or multiselect) it is stored in the first availability and shared for all items
    const availabilityToCopy = canUseSameSessionShortcut ? availability : allAvailabilities[0];
    for (const variant of availabilityToCopy.variants) {
      const sessGid = availability.session_group_id;
      variantId++;
      if (firstVariant == null) {
        firstVariant = variant;
      }

      // Because we are using the shortcut, we need to tell the backend to consider those items as separate in the cart (just like if we went back to P2)
      const sessionItem = createSessionItem(variant, sessGid, variantId, selectedStaff);
      if (canUseSameSessionShortcut) {
        sessionItem['cart_group_id'] = availability.cart_group_id ?? cartGroupId;
      }
      formattedData.items.push(sessionItem);

      const ffvMapKey = sessionItem.client ? `${sessionItem.client.first_name}-${sessionItem.client.last_name}` : 'NO_CLIENT';
      for (const option of variant.custom_options) {
        if (!option.selectedResource) continue;

        // FFV: we give 1 licence per participant max
        const associatedResource = findArrayItemById(option.selectedResource, option.resources);
        if (associatedResource.partner_tag === PARTNER_TAG.FFV) {
          if (ffvLicenceMap[ffvMapKey] === true) {
            devMessage('FFV licence given on another session already for this participant:', ffvMapKey);
            continue;
          }

          ffvLicenceMap[ffvMapKey] = true;
        }

        formattedData.items.push(createSessionResourceItem(sessGid, variant.participant, option, variantId, selectedStaff, sessionItem['price']));
      }
    }
  }

  if (firstVariant != null && firstVariant.cart_rule_data != null) {
    Object.assign(formattedData, firstVariant.cart_rule_data);
  }
  return formattedData;
};

const getInitialFormattedData = (teamId, storedCartId) => {
  const validCartId = getValidUuidOrNull(storedCartId);
  const formattedData = { items: [] };
  formattedData[validCartId ? 'order_id' : 'team_id'] = validCartId ?? teamId;
  formattedData['origin'] = getReferrerHost();
  return formattedData;
};

const createSessionItem = (variant, sessionGroupId, variantId, selectedStaff) => {
  const variantPrice = getVariantSelectedPrice(variant);
  const quantity = checkQuantity(variant.participant);
  const sessionItem = {
    session_group_id: sessionGroupId,
    price: variantPrice ?? 0,
    price_without_promo: getVariantSelectedPrice(variant, null, true),
    quantity: quantity,
    variant_id: variantId,
  };

  if (variant.participantDetails) {
    sessionItem['client'] = handleParticipantDataForBackend(variant.participantDetails.client);
  }

  if (variant.currentPriceVariationId !== STANDARD_PRICE_SELECT_VALUE && variantPrice != null) {
    sessionItem['price_variation_id'] = variant.currentPriceVariationId;
  }

  if (selectedStaff) {
    sessionItem['desired_staff_id'] = selectedStaff.user_id;
  }

  if (variant.cart_rule_data.cart_rule_id) {
    sessionItem['cart_rule_id'] = variant.cart_rule_data.cart_rule_id;
  }

  if (variant.marketing_rule_data.marketing_rule_id) {
    sessionItem['marketing_rule_id'] = variant.marketing_rule_data.marketing_rule_id;
  }

  return sessionItem;
};

const createSessionResourceItem = (sessionGroupId, itemQuantity, option, variantId, selectedStaff, priceValue) => {
  const resourceId = option.selectedResource;
  const resource = findArrayItemById(resourceId, option.resources);
  const quantity = checkQuantity(itemQuantity);
  const formattedItem = {
    resource_target_group_id: sessionGroupId,
    resource_id: resourceId,
    price: getOptionPrice(resource, priceValue),
    quantity: quantity,
    variant_id: variantId,
  };

  if (selectedStaff) {
    formattedItem['desired_staff_id'] = selectedStaff.user_id;
  }

  return formattedItem;
};

const createSingleResourceItem = (resource, resourceIdToUse) => {
  const quantity = checkQuantity(resource.quantity);
  const formattedItem = {
    resource_id: resourceIdToUse,
    price: resource.price,
    quantity: quantity,
  };
  return formattedItem;
};

const createNominativeResourceItems = (formattedData, shopResource, resourceIdToUse) => {
  if (shopResource.namingData == null || shopResource.namingData.length === 0) throw new Error();

  shopResource.namingData.forEach((namingItem, index) => {
    const formattedItem = {
      resource_id: resourceIdToUse,
      price: shopResource.price,
      quantity: 1,
      client: handleParticipantDataForBackend(namingItem),
    };
    formattedData.items.push(formattedItem);
  });
};

const formatPreCartDataForShop = (shopResource, shopResourceChild, teamId, storedCartId) => {
  const formattedData = getInitialFormattedData(teamId, storedCartId);

  const resourceIdToUse = shopResourceChild ? shopResourceChild.id : shopResource.id;
  if (shopResource.nominative) {
    createNominativeResourceItems(formattedData, shopResource, resourceIdToUse);
  } else {
    formattedData.items.push(createSingleResourceItem(shopResource, resourceIdToUse));
  }
  return formattedData;
};

const checkQuantity = quantity => {
  const parsedQuantity = parseInt(quantity);
  if (!isValidQuantityNumber(parsedQuantity)) throw new Error();
  return parsedQuantity;
};
